/**
 * Builds and maintains the thumbnail and table-of-contents sidebars.
 */
export default class SidebarPanels {
  #eventCleanups = [];

  #buttons = {};

  constructor({
    bookWrapper,
    idPrefix,
    bookIndex,
    thumbnailPages,
    toc,
    gestureController,
  }) {
    this.gestureController = gestureController;

    if (thumbnailPages) {
      this.thumbnails = this.#buildThumbnails(
        bookWrapper,
        `${idPrefix}-thumbnails`,
        thumbnailPages,
      );
    }
    if (this.#hasToc(toc)) {
      const tocPanel = this.#buildToc(
        bookWrapper,
        `${idPrefix}-toc`,
        bookIndex,
        toc,
      );
      this.tocContainer = tocPanel.container;
      this.tocElement = tocPanel.element;
    }
  }

  get hasToc() {
    return Boolean(this.tocContainer);
  }

  setButtons(buttons) {
    this.#buttons = buttons;
    this.#setOpen(
      this.tocContainer,
      this.#buttons.toc,
      this.tocContainer?.classList.contains("open") ?? false,
    );
    this.#setOpen(
      this.thumbnails?.book,
      this.#buttons.thumbnails,
      this.thumbnails?.book.classList.contains("open") ?? false,
    );
  }

  toggleToc(force) {
    if (!this.tocContainer) return false;

    const open = this.#toggle(this.tocContainer, this.#buttons.toc, force);
    if (open) {
      this.#setOpen(this.thumbnails?.book, this.#buttons.thumbnails, false);
    }
    return open;
  }

  toggleThumbnails(force) {
    if (!this.thumbnails?.book) return false;

    const open = this.#toggle(
      this.thumbnails.book,
      this.#buttons.thumbnails,
      force,
    );
    if (open) {
      this.#setOpen(this.tocContainer, this.#buttons.toc, false);
    }
    return open;
  }

  destroy() {
    for (const cleanup of this.#eventCleanups.splice(0).reverse()) cleanup();
  }

  #buildThumbnails(bookWrapper, id, pages) {
    const book = document.createElement("div");
    book.classList.add("rhea-thumbnails");
    book.id = id;
    this.#setOpen(book, undefined, false);

    const gridder = document.createElement("div");
    gridder.classList.add("rhea-thumbnails-gridder");
    const root = document.createElement("ul");
    root.classList.add("rhea-thumbnails-list");

    const paddedPages = [...pages];
    paddedPages.unshift(document.createElement("li"));
    if (paddedPages.length % 2 > 0) {
      paddedPages.push(document.createElement("li"));
    }
    root.append(...paddedPages);
    gridder.append(root);
    book.append(gridder);
    bookWrapper.append(book);

    this.gestureController.attach(root);

    return { book, gridder, root, pages: paddedPages };
  }

  #buildToc(bookWrapper, id, bookIndex, toc) {
    const container = document.createElement("div");
    container.id = id;
    container.classList.add("rhea-toc-container");
    this.#setOpen(container, undefined, false);

    const gridder = document.createElement("div");
    gridder.classList.add("rhea-toc-gridder");
    const element = document.createElement("ol");
    element.classList.add("rhea-toc");
    element.append(
      ...toc.map((entry) => this.#buildTocEntry(entry, bookIndex)),
    );
    gridder.append(element);
    container.append(gridder);
    bookWrapper.append(container);

    this.#listen(element, "click", (event) => {
      if (event.target.tagName === "A") this.toggleToc(false);
    });
    this.gestureController.attach(element);

    return { container, element };
  }

  #buildTocEntry(entry, bookIndex) {
    const item = document.createElement("li");
    item.classList.add("rhea-toc__item");
    const link = document.createElement("a");
    link.href = `#${bookIndex}-${entry.page}`;
    link.textContent = entry.title;
    item.append(link);

    if (entry.pages?.length > 0) {
      const children = document.createElement("ol");
      children.classList.add(
        "rhea-toc__menu",
        `rhea-toc__level--${entry.level + 1}`,
      );
      children.append(
        ...entry.pages.map((child) => this.#buildTocEntry(child, bookIndex)),
      );
      item.append(children);
    }
    return item;
  }

  #toggle(panel, button, force) {
    const open =
      force === undefined ? !panel.classList.contains("open") : Boolean(force);
    return this.#setOpen(panel, button, open);
  }

  #setOpen(panel, button, open) {
    if (!panel) return false;

    panel.classList.toggle("open", open);
    panel.toggleAttribute("inert", !open);
    panel.setAttribute("aria-hidden", String(!open));
    button?.setAttribute("aria-expanded", String(open));
    return open;
  }

  #hasToc(toc) {
    return Array.isArray(toc) && toc.length > 0 && toc[0] !== null;
  }

  #listen(target, eventName, listener) {
    target.addEventListener(eventName, listener);
    this.#eventCleanups.push(() =>
      target.removeEventListener(eventName, listener),
    );
  }
}
